import { Injectable, inject } from '@angular/core';

import { Equipment, Option, Plan, ProductFactory } from '@bytel/bytel-sales';

import { AccessoryService } from '@services/accessory.service';
import { Ftth3ClicsService } from '@services/ftth3-clics.service';
import { MainCartService } from '@services/main-cart.service';
import { OptionsService } from '@services/options.service';

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';

import { Step } from './step';

@Injectable({
    providedIn: 'root',
})
export class CrossellStep extends Step {
    public static readonly key: string = 'summary';
    public isValid: boolean;
    public label: string = 'Compléments';
    public next: Step;
    public routerLink: string = '/tunnel/recapitulatif';
    public hasReminder = false;
    protected cartService = inject(MainCartService);
    private accessoryService = inject(AccessoryService);
    private optionsService = inject(OptionsService);
    private ftth3ClicsService = inject(Ftth3ClicsService);
    private _hasAccessories: boolean = false;
    private _hasOptions: boolean = false;

    constructor() {
        super();

        this.cartService.cartLoadedFromStorageObs.subscribe(() => {
            forkJoin([this._checkAccessories(), this._checkOptions()]).subscribe(() => {
                this._needStepperRefresh.next(null);
                this._isLoaded.next(true);
            });
        });

        this.cartService.productActionObs.subscribe((data) => {
            let obs: Observable<boolean>;
            if (ProductFactory.Is(data.product, Equipment)) {
                obs = this._checkAccessories();
            } else if (ProductFactory.Is(data.product, Plan) || ProductFactory.Is(data.product, Option)) {
                obs = this._checkOptions();
            }
            if (obs) {
                obs.subscribe(() => {
                    this._needStepperRefresh.next(null);
                });
            }
        });
    }

    public canAccess(): Observable<boolean> {
        if (this.cartService.isPreCrossSell()) {
            return of(false);
        }
        return this.hasOptionOrAccessory();
    }

    public canDisplay(): boolean {
        return (
            !this.cartService.isPreCrossSell() &&
            (this._hasOptions || this._hasAccessories) &&
            !this.ftth3ClicsService.isActive()
        );
    }

    private hasOptionOrAccessory(): Observable<boolean> {
        return forkJoin([this._checkAccessories(), this._checkOptions()]).pipe(
            map((results) => {
                const hasOptionOrAccessory = results.some((result) => result);
                return hasOptionOrAccessory && !this.ftth3ClicsService.isActive();
            }),
        );
    }

    private _checkAccessories(): Observable<boolean> {
        this._hasAccessories = this.accessoryService.hasCrosssellInCart(this.cartService.cartModel);
        return of(this._hasAccessories);
    }

    private _checkOptions(): Observable<boolean> {
        if (!document.location.href.includes('tunnel/redirect')) {
            return this.optionsService.initializeOptions(this.cartService.cartModel).pipe(
                catchError(() => of([])),
                mergeMap(() => this.optionsService.getSortedOptionsByQuote(this.cartService.getQuote())),
                tap((options) => {
                    const filterableOptions: Option[] = this.optionsService.excludeParentalOptions(options);
                    this._hasOptions = !!filterableOptions.length;
                }),
                map((options) => !!options.length),
            );
        }
    }
}
